import * as t from "io-ts";
import { Difficulties, PhaseOfPlay, PlayModes, PlayPositions } from "../../enums";
import { boolOrIntCodec } from "../../validation/codecs";
import { enumCodec, mixed } from "../../validation/validator";
import { CallResponseMixinSchema, CallResponseSchema } from "../common/callResponseSchema";
import { TeamConfigResponseSchema } from "../teams/teamSchemas";

const commonScenarioFields = {
  id: t.number,
  doc_id: t.union([t.string, t.undefined, t.null]),
  created_at: t.number,
  updated_at: t.union([t.number, t.undefined, t.null]),
  created_by: t.union([t.string, t.undefined, t.null]),
  updated_by: t.union([t.string, t.undefined, t.null]),
  published: t.boolean,
  deleted: t.boolean,
  name: t.string,
  description: t.string,
  difficulty: enumCodec(Difficulties),
  play_positions: t.array(enumCodec(PlayPositions)),
  phase_of_play: enumCodec(PhaseOfPlay),
  pressure_time: t.number,
  length: t.number,
  editor_version_draft: t.string,
  editor_version_published: t.string,
};

export const ScenarioRecordSchema = t.type({
  ...commonScenarioFields,
  storage_bucket_id: t.number,
  storage_file_path: t.string,
});

export const ScenarioResponseSchema = t.type({
  ...commonScenarioFields,
  created_by_doc_id: t.string,
  created_by_first_name: t.string,
  created_by_last_name: t.string,
  updated_by_doc_id: t.union([t.string, t.undefined, t.null]),
  updated_by_first_name: t.union([t.string, t.undefined, t.null]),
  updated_by_last_name: t.union([t.string, t.undefined, t.null]),
  playlists: t.array(
    t.type({
      id: t.number,
      name: t.string,
    })
  ),
  tags: t.array(t.string),
  project_id: t.union([t.number, t.undefined, t.null]),
});

export const PlaylistScenarioResponseSchema = t.intersection([
  t.type({
    scenario_id: t.number,
    scenario: ScenarioResponseSchema,
  }),
  t.partial({
    game_speed: t.union([t.number, t.null]),
    bullet_time_decisions: t.union([boolOrIntCodec, t.null]),
    memory_player_count: t.union([t.number, t.null]),
    memory_time_limit: t.union([t.number, t.null]),
    memory_distance: t.union([t.number, t.null]),
    mirrored: t.union([boolOrIntCodec, t.null]),
    index: t.union([t.number, t.null]),
  }),
]);

export const GetScenarioResponseSchema = t.type({
  metadata: ScenarioResponseSchema,
  download_url: t.string,
});

export const ScenarioConfigSchema = t.type({
  id: t.union([t.number, t.string]),
  game_speed: t.union([t.number, t.undefined, t.null]),
  bullet_time_decisions: t.union([boolOrIntCodec, t.undefined, t.null]),
  memory_player_count: t.union([t.number, t.undefined, t.null]),
  memory_time_limit: t.union([t.number, t.undefined, t.null]),
  memory_distance: t.union([t.number, t.undefined, t.null]),
  mirrored: t.union([boolOrIntCodec, t.undefined, t.null]),
  // Kit color theme - (HEX color code)
  team_a_color_primary: t.union([t.string, t.undefined, t.null]),
  team_a_color_secondary: t.union([t.string, t.undefined, t.null]),
  team_a_color_tertiary: t.union([t.string, t.undefined, t.null]),
  team_a_color_gk_primary: t.union([t.string, t.undefined, t.null]),
  team_a_color_gk_secondary: t.union([t.string, t.undefined, t.null]),
  team_a_color_gk_tertiary: t.union([t.string, t.undefined, t.null]),
  team_b_color_primary: t.union([t.string, t.undefined, t.null]),
  team_b_color_secondary: t.union([t.string, t.undefined, t.null]),
  team_b_color_tertiary: t.union([t.string, t.undefined, t.null]),
  team_b_color_gk_primary: t.union([t.string, t.undefined, t.null]),
  team_b_color_gk_secondary: t.union([t.string, t.undefined, t.null]),
  team_b_color_gk_tertiary: t.union([t.string, t.undefined, t.null]),
  // Order index
  index: t.union([t.number, t.undefined, t.null]),
  // Prompts
  team_a_name: t.union([t.string, t.undefined, t.null]),
  team_b_name: t.union([t.string, t.undefined, t.null]),
  // team configs
  team_a_config: t.union([t.number, t.undefined, t.null]),
  team_b_config: t.union([t.number, t.undefined, t.null]),
});

export const ScenarioConfigRecordSchema = t.intersection([
  ScenarioConfigSchema,
  t.type({
    doc_id: t.union([t.string, t.undefined, t.null]),
  }),
]);

export const ScenarioConfigResponseSchema = t.intersection([
  t.type({
    team_a_config: t.union([TeamConfigResponseSchema, t.undefined, t.null]),
    team_b_config: t.union([TeamConfigResponseSchema, t.undefined, t.null]),
  }),
  ScenarioConfigRecordSchema,
]);

export type ScenarioConfigResponse = t.TypeOf<typeof ScenarioConfigResponseSchema>;

const ScenarioLegacySchema = t.intersection([
  t.type({
    id: t.number,
    doc_id: t.string,
    author: t.string,
    play_positions: t.array(enumCodec(PlayPositions)),
    creation_date: t.number,
    type: t.string,
    play_modes: enumCodec(PlayModes),
    storage_id: t.string,
    storage_file_location: t.string,
    difficulty: enumCodec(Difficulties),
    name: t.string,
    description: t.string,
    published: t.boolean,
    prod_storage_file_location: t.string,
    storage_file_size: t.string,
    editor_version: t.string,
    prod_editor_version: t.string,
    version: t.number,
    length: t.number,
  }),
  t.partial({
    prod_version: t.number,
    pass_options: t.array(
      t.type({
        position: enumCodec(PlayPositions),
        priority: t.number,
      })
    ),
    pressure_time: t.type({
      seconds: t.number,
      operator: t.string,
    }),
    phase_of_play: enumCodec(PhaseOfPlay),
    receival_zone: t.number,
    deleted: t.boolean,
    owners: t.array(
      t.type({
        id: t.string,
        type: t.string,
      })
    ),
  }),
]);

export type ScenarioLegacyRecord = t.TypeOf<typeof ScenarioLegacySchema>;
export type ScenarioConfigRecord = t.TypeOf<typeof ScenarioConfigRecordSchema>;
export type ScenarioRecord = t.TypeOf<typeof ScenarioRecordSchema>;
export type ScenarioConfig = t.TypeOf<typeof ScenarioConfigSchema>;
// export type EnrichedScenarioRecord = ScenarioRecord & { config: ScenarioConfigRecord };
export type ScenarioResponse = t.TypeOf<typeof ScenarioResponseSchema>;
export type PlaylistScenarioResponse = t.TypeOf<typeof PlaylistScenarioResponseSchema>;

export type MinimalScenarioResponse = 
{
  doc_id: string | null | undefined;
  name: string | null| undefined;
}

// API Schemas

export const CreateScenarioSchema = t.type({
  account_id: t.union([t.number, t.null]),
  name: t.string,
  description: t.string,
  play_positions: t.array(enumCodec(PlayPositions)),
  phase_of_play: enumCodec(PhaseOfPlay),
  difficulty: enumCodec(Difficulties),
  pressure_time: t.number,
  scenario_data: t.any,
  tags: t.array(t.string),
});

export const GetScenariosSchema = t.intersection([
  t.type({
    account_id: t.union([t.number, t.null]),
  }),
  t.partial({
    scenario_ids: t.union([t.array(t.string), t.array(t.number), t.null]),
    search_query: t.string,
    filter_play_positions: t.union([t.array(enumCodec(PlayPositions)), t.undefined, t.null]),
    filter_tags: t.union([t.array(t.string), t.undefined, t.null]),
    filter_phase_of_play: t.union([enumCodec(PhaseOfPlay), t.undefined, t.null]),
    filter_published: t.union([t.boolean, t.undefined, t.null]),
    include_public: t.boolean,
    offset: t.number,
    limit: t.number,
  }),
]);

export const GetPlaylistScenariosSchema = t.intersection([
  t.type({
    account_id: t.union([t.number, t.null]),
    playlist_id: t.number,
  }),
  t.partial({
    include_public: t.boolean,
    scenario_ids: t.union([t.array(t.string), t.array(t.number), t.null]),
    search_query: t.string,
    filter_play_positions: t.union([t.array(enumCodec(PlayPositions)), t.undefined, t.null]),
    filter_tags: t.union([t.array(t.string), t.undefined, t.null]),
    filter_phase_of_play: t.union([enumCodec(PhaseOfPlay), t.undefined, t.null]),
    filter_published: t.union([t.boolean, t.undefined, t.null]),
    offset: t.number,
    limit: t.number,
  }),
]);

export const GetScenarioSchema = t.type({
  scenario_id: t.string,
  is_staging: t.boolean,
});

export const UpdateScenarioSchema = t.intersection([
  t.type({
    scenario_id: t.string,
  }),
  t.partial({
    name: t.union([t.string, t.undefined, t.null]),
    description: t.union([t.string, t.undefined, t.null]),
    play_positions: t.union([t.array(enumCodec(PlayPositions)), t.undefined, t.null]),
    phase_of_play: t.union([enumCodec(PhaseOfPlay), t.undefined, t.null]),
    difficulty: t.union([t.string, t.undefined, t.null]),
    pressure_time: t.union([t.number, t.undefined, t.null]),
    scenario_data: t.union([t.any, t.undefined, t.null]),
    published: t.union([t.boolean, t.undefined, t.null]),
    tags: t.union([t.array(t.string), t.undefined, t.null]),
  }),
]);

export const PublishScenarioSchema = t.type({
  scenario_id: t.string,
});

export const DeleteScenarioSchema = t.type({
  scenario_id: t.string,
  secure_key: t.string,
});

export const GetScenarioPoolSchema = t.intersection([
  t.type({
    account_id: t.union([t.number, t.null]),
  }),
  t.partial({
    play_positions: t.array(enumCodec(PlayPositions)),
    phases_of_play: t.array(enumCodec(PhaseOfPlay)),
    tags: t.array(t.string),
    include_public: t.union([t.boolean, t.undefined, t.null]),
    include_unpublished: t.union([t.boolean, t.undefined, t.null]),
  }),
]);

// Client API
export const ScenarioApis = {
  Scenarios: {
    // CREATE
    CreateScenario: {
      Name: "scenarios-createScenarioV2",
      Request: CreateScenarioSchema,
      Response: mixed([
        t.type({
          data: t.type({
            scenario_id: t.string,
          }),
        }),
        CallResponseMixinSchema,
      ]),
    },

    // READ
    GetScenario: {
      Name: "scenarios-getScenarioV3",
      Request: GetScenarioSchema,
      Response: mixed([
        t.type({
          data: GetScenarioResponseSchema,
        }),
        CallResponseMixinSchema,
      ]),
    },
    GetScenarios: {
      Name: "scenarios-getScenariosV2",
      Request: GetScenariosSchema,
      Response: mixed([
        t.type({
          data: t.type({
            total: t.number,
            scenarios: t.array(ScenarioResponseSchema),
          }),
        }),
        CallResponseMixinSchema,
      ]),
    },
    GetPlaylistScenarios: {
      Name: "scenarios-getPlaylistScenarios",
      Request: GetPlaylistScenariosSchema,
      Response: mixed([
        t.type({
          data: t.type({
            total: t.number,
            scenarios: t.array(PlaylistScenarioResponseSchema),
          }),
        }),
        CallResponseMixinSchema,
      ]),
    },
    GetScenarioPool: {
      Name: "scenarios-getScenarioPool",
      Request: GetScenarioPoolSchema,
      Response: mixed([
        t.type({
          data: t.array(ScenarioRecordSchema),
        }),
        CallResponseMixinSchema,
      ]),
    },

    // UPDATE
    UpdateScenario: {
      Name: "scenarios-updateScenarioV2",
      Request: UpdateScenarioSchema,
      Response: CallResponseSchema,
    },
    PublishScenario: {
      Name: "scenarios-publishScenario",
      Request: PublishScenarioSchema,
      Response: CallResponseSchema,
    },

    // DELETE
    DeleteScenario: {
      Name: "scenarios-deleteScenarioV2",
      Request: DeleteScenarioSchema,
      Response: CallResponseSchema,
    },
  },
};
